import Anthropic from '@anthropic-ai/sdk';

// Moods the character's tone can be judged as, in a single structured call
const MOOD_VALUES = ['angry', 'condescending', 'suspicious', 'sad', 'bored', 'neutral', 'curious', 'happy', 'excited'];

const MODEL = 'claude-haiku-4-5'; // the most cost-effective model

// Prompts are built server-side (see server/prompts.js). The browser sends a
// spec — which personality, which character, which mode — and gets back the
// finished text. A spec is fixed for the life of a conversation, so the result
// is cached and each conversation costs at most one round-trip.
const promptCache = new Map();

export const fetchPrompt = async (spec) => {
  const key = JSON.stringify(spec);
  if (!promptCache.has(key)) {
    promptCache.set(
      key,
      fetch('/api/prompt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(spec),
      }).then((res) => {
        if (!res.ok) throw new Error(`Prompt request failed: ${res.status}`);
        return res.json();
      })
    );
  }
  try {
    return await promptCache.get(key);
  } catch (error) {
    promptCache.delete(key); // a failed fetch shouldn't poison the cache
    throw error;
  }
};

const cached = (text) => [{ type: 'text', text, cache_control: { type: 'ephemeral' } }];

// Run a completion either directly against Anthropic (bring-your-own-key, in
// the browser) or through our trial proxy (owner's key, server-side). `auth` is
// either { apiKey } or { trial: true }. On the trial path the spec goes over the
// wire and the server builds the system prompt itself; on the bring-your-own-key
// path we fetch the prompt first and send it to Anthropic ourselves. Returns
// { ok, content } on success, or { ok: false, code, status } on failure (code
// drives app-level handling, e.g. 'owner_out_of_credits' / 'trial_exhausted').
const complete = async (auth, promptSpec, params, conversationId) => {
  if (auth && auth.trial) {
    let res;
    try {
      res = await fetch('/api/trial/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          conversationId,
          prompt: promptSpec,
          messages: params.messages,
          output_config: params.output_config,
          max_tokens: params.max_tokens,
        }),
      });
    } catch (e) {
      return { ok: false, code: 'error', status: 'network' };
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      return { ok: false, code: body.code || 'error', status: res.status };
    }
    const data = await res.json();
    return { ok: true, content: data.content };
  }

  // Bring-your-own-key: talk to Anthropic straight from the browser.
  const { system } = await fetchPrompt(promptSpec);
  const client = new Anthropic({ apiKey: auth.apiKey, dangerouslyAllowBrowser: true });
  const message = await client.messages.create({ ...params, model: MODEL, system: cached(system) });
  console.log('[anthropic] usage:', message.usage);
  return { ok: true, content: message.content };
};

const hasCredentials = (auth) => !!(auth && (auth.trial || auth.apiKey));

// Send a message to Claude and get back the in-character reply plus the
// character's auto-detected mood (and, in Game Mode, whether they just
// genuinely agreed to a date) — all from a single structured-output call.
export const sendStructuredMessage = async (
  auth,
  promptSpec,
  messages,
  { includeDateAccepted = false, conversationId } = {}
) => {
  if (!hasCredentials(auth)) {
    return { success: false, code: 'error', error: 'No credentials provided' };
  }

  const schema = {
    type: 'object',
    properties: {
      reply: {
        type: 'string',
        description: "The character's in-character chat reply, written exactly as they'd text it — this is what gets shown to the user."
      },
      mood: {
        type: 'string',
        enum: MOOD_VALUES,
        description: "The character's actual emotional tone in this reply, based on how the conversation has gone so far."
      },
      ...(includeDateAccepted && {
        dateAccepted: {
          type: 'boolean',
          description: 'True only if, in this reply, the character is naturally and genuinely agreeing to go on an actual date with the user — not a joke, a hypothetical, or a noncommittal maybe.'
        }
      })
    },
    required: ['reply', 'mood', ...(includeDateAccepted ? ['dateAccepted'] : [])],
    additionalProperties: false
  };

  const params = {
    messages,
    max_tokens: 1000,
    output_config: { format: { type: 'json_schema', schema } }
  };

  try {
    const result = await complete(auth, promptSpec, params, conversationId);
    if (!result.ok) {
      return { success: false, code: result.code, status: result.status };
    }
    const parsed = JSON.parse(result.content[0].text);
    return {
      success: true,
      reply: parsed.reply,
      mood: parsed.mood,
      dateAccepted: includeDateAccepted ? !!parsed.dateAccepted : false,
    };
  } catch (error) {
    console.error('Error calling Anthropic API:', error);
    if (error.status === 401) {
      return {
        success: false,
        code: 'auth',
        error: 'Authentication failed. Please check your API key format and validity.',
      };
    }
    return { success: false, code: 'error', error: error.message, status: error.status || 'unknown' };
  }
};

// Generate feedback based on the conversation. The coaching instructions live
// server-side; only the transcript is stitched in here, so a bring-your-own-key
// user's conversation still never reaches our server.
export const generateFeedback = async (auth, conversation, gameMode = false, { conversationId } = {}) => {
  if (!hasCredentials(auth)) {
    return { success: false, code: 'error', error: 'No credentials provided' };
  }

  const promptSpec = { kind: 'feedback', gameMode };

  const conversationText = conversation
    .map(msg => `${msg.role === 'user' ? 'USER' : 'DATE'}: ${msg.content}`)
    .join('\n\n');

  try {
    const { userTemplate } = await fetchPrompt(promptSpec);
    const params = {
      // Function replacement: a literal `$&` in the transcript must not be read
      // as a substitution pattern.
      messages: [{ role: 'user', content: userTemplate.replace('{{transcript}}', () => conversationText) }],
      max_tokens: 1500,
    };

    const result = await complete(auth, promptSpec, params, conversationId);
    if (!result.ok) {
      return { success: false, code: result.code, status: result.status };
    }
    return { success: true, feedback: result.content[0].text };
  } catch (error) {
    console.error('Error generating feedback:', error);
    return { success: false, code: 'error', error: error.message, status: error.status || 'unknown' };
  }
};
